import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Form, Button } from 'antd';
import { useGenericQuery } from '../queries/use-generic-query';
import { useGenericMutation } from '../queries/use-generic-mutation';
import CommonForm from './global/form';
import Spinner from './global/spinner';
import SOURCES from '../utils/constants';

const siteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY_LIVE;

const LeadFormComponent = () => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const uniqueCode = queryParams.get('uniqueCode');
    const [form] = Form.useForm();

    const { data: weblink, isLoading: isWeblinkLoading } = useGenericQuery({
        queryKey: ['webform', uniqueCode],
        endpoint: `/weblink/present/webform`,
        queryParams: { uniqueCode },
        enabled: !!uniqueCode,
    });

    const saveMutation = useGenericMutation({
        endpoint: `/weblink/present/webform`,
    });

    const formData = useMemo(() => {
        const settings = weblink?.settings;
        if (!settings) return [];
        
        const fields = [];

        // Helper to check both PascalCase and camelCase keys to be safe
        const isHidden = (key) => settings[`${key}Hidden`] === true || settings[`${key.charAt(0).toLowerCase() + key.slice(1)}Hidden`] === true;
        const isRequired = (key) => settings[`${key}Required`] === true || settings[`${key.charAt(0).toLowerCase() + key.slice(1)}Required`] === true;
        
        // Special mapping for Notes/Inquiry due to the slash in API response
        const isNotesHidden = settings['Notes/InquiryHidden'] === true || settings.notesInquiryHidden === true || settings.notesInquiryHidden === "true";
        const isNotesRequired = settings['Notes/InquiryRequired'] === true || settings.notesInquiryRequired === true || settings.notesInquiryRequired === "true";
        
        if (!isHidden('FirstName')) {
            fields.push({
                label: "First Name",
                name: "firstName",
                type: "text",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isRequired('FirstName'),
                placeholder: "Enter first name",
                span: 12,
            });
        }

        if (!isHidden('LastName')) {
            fields.push({
                label: "Last Name",
                name: "lastName",
                type: "text",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isRequired('LastName'),
                placeholder: "Enter last name",
                span: 12,
            });
        }

        if (!isHidden('Phone')) {
            fields.push({
                label: "Phone",
                name: "phone",
                type: "text",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isRequired('Phone'),
                placeholder: "Enter phone number",
                span: 12,
            });
        }

        if (!isHidden('Email')) {
            fields.push({
                label: "Email",
                name: "email",
                type: "email",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isRequired('Email'),
                placeholder: "Enter email address",
                span: 12,
            });
        }

        if (!isHidden('EventName')) {
            fields.push({
                label: "Event Name",
                name: "eventName",
                type: "text",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isRequired('EventName'),
                placeholder: "Enter event name",
                span: 12,
            });
        }

        if (!isHidden('EventDate')) {
            fields.push({
                label: "Event Date",
                name: "eventDate",
                inputType: SOURCES.FORMINPUTTYPES.DATE,
                required: isRequired('EventDate'),
                placeholder: "Select event date",
                span: 12,
            });
        }

        if (!isHidden('EventType')) {
            fields.push({
                label: "Event Type",
                name: "eventType",
                type: "text",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isRequired('EventType'),
                placeholder: "Enter event type",
                span: 12,
            });
        }

        if (!isHidden('Budget')) {
            fields.push({
                label: "Budget",
                name: "budget",
                type: "number",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isRequired('Budget'),
                placeholder: "Enter budget",
                span: 12,
            });
        }

        if (!isHidden('GuestCount')) {
            fields.push({
                label: "Guest Count",
                name: "guestCount",
                type: "number",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isRequired('GuestCount'),
                placeholder: "Enter guest count",
                span: 12,
            });
        }

        if (!isHidden('Company')) {
            fields.push({
                label: "Company",
                name: "company",
                type: "text",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isRequired('Company'),
                placeholder: "Enter company name",
                span: 12,
            });
        }

        if (!isHidden('ServiceAddress')) {
            fields.push({
                label: "Service Address",
                name: "address",
                type: "text",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isRequired('ServiceAddress'),
                placeholder: "Enter service address",
                span: 12,
            });
        }

        if (!isNotesHidden) {
            fields.push({
                label: "Notes / Inquiry",
                name: "notesInquiry",
                type: "textarea",
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                required: isNotesRequired,
                placeholder: "Enter any notes or inquiry details",
                span: 12,
            });
        }

        return fields;
    }, [weblink]);

    const handleFinish = async (values) => {
        try {
            const captchaToken = await executeRecaptcha("submit_form");
            
            const Post = {
                uniqueCode,
                firstName: values.firstName,
                lastName: values.lastName,
                phone: values.phone,
                email: values.email,
                eventDate: values.eventDate ? values.eventDate.format('YYYY-MM-DD') : values.eventDate,
                eventYear: values.eventDate ? values.eventDate.format('YYYY') : values.eventYear,
                eventType: values.eventType,
                eventName: values.eventName,
                guestCount: values.guestCount,
                budget: values.budget,
                company: values.company,
                address: values.address,
                inquiry: values.notesInquiry,
                captchaToken
            };

            await saveMutation.mutateAsync(Post);
        } catch (error) {
            console.error(error);
        }
    };

    if (isWeblinkLoading) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center">
                <Spinner className="w-8 h-8 !border-primary" />
            </div>
        );
    }

    return (
        <div className="w-full flex items-center justify-center bg-gray-50 min-h-screen p-4 md:p-8">
            <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl p-6 md:p-10 animate-in fade-in duration-500">
                <div className="mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 tracking-tight">
                        {weblink?.formName || 'Lead Form'}
                    </h3>
                </div>

                {saveMutation.isSuccess ? (
                    <div className="flex flex-col items-center justify-center py-12 text-teal-600 bg-teal-50 rounded-xl border border-teal-100">
                        <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h5 className="text-xl font-bold">Contact submitted successfully!</h5>
                        <p className="mt-2 text-teal-700">Thank you for getting in touch with us.</p>
                    </div>
                ) : (
                    <div>
                        <CommonForm
                            form={form}
                            data={formData}
                            onFinish={handleFinish}
                            isInternalSubmitt={false}
                        />

                        <div className="mt-8 flex justify-start">
                            <Button
                                type="primary"
                                onClick={() => form.submit()}
                                disabled={saveMutation.isPending}
                                className="w-full sm:w-auto min-w-[160px] h-12 text-base font-bold rounded-xl shadow-lg shadow-primary/25 !bg-primary hover:!bg-primary/90 border-none flexitems-center justify-center gap-x-2"
                            >
                                {saveMutation.isPending && <Spinner className="!border-white w-4 h-4" />}
                                Submit
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const LeadGenerateForm = () => {
    return (
        <GoogleReCaptchaProvider reCaptchaKey={siteKey}>
            <LeadFormComponent />
        </GoogleReCaptchaProvider>
    );
};

export default LeadGenerateForm;